import { Spacing } from '@persona-id/design-tokens';
import produce from 'immer';
import { debounce, xor } from 'lodash/fp';
import { ComponentProps, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { BORDER_RADIUSES } from 'shared/styles/variables';
import styled from 'styled-components';

import {
  LOCALE_TO_EXCLUDE_TRANSLATIONS,
  UNSUPPORTED_LANGUAGE_TEXT,
} from 'dashboard/apps/InquiryTemplateEditor/forms/FormSectionTranslationsConfig/FormSectionTranslationsConfig';
import { selector } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/constants';
import { SectionDescription } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateEditor/shared';
import { TranslationsConfig } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ComponentEditorSections/TranslationsEditor';
import TranslateAllButton from 'dashboard/apps/InquiryTemplateEditor/TranslateAllButton';
import Accordion from 'dashboard/components/Accordion';
import FormControlLabel from 'dashboard/components/FormControls/FormControlLabel';
import RichTextEditor from 'dashboard/components-lib/RichTextEditor';
import FormGroup from 'dashboard/layouts/FormLayout/FormGroup';
import useTranslations from 'dashboard/lib/hooks/useTranslations';
import { StepLocalizedStrings } from 'dashboard/redux/slices/inquiry-template';
import { RootState } from 'dashboard/redux/store';
import Button from 'design-system/components/Button';
import Input, { InputGroup } from 'design-system/components/Input';
import TextArea from 'design-system/components/TextArea';
import { paragraphMixins } from 'design-system/mixins/text';
import {
  FLE_COMMON_LOCALES,
  INQUIRY_LANGUAGES_ENGLISH,
  InquiryIso6391LanguageCode,
} from 'lib/constants/locales';
import { Translation } from 'lib/interfaces/ui-components';

const placeholder = 'No custom translations';

const StyledFormGroup = styled(FormGroup)`
  display: flex;
  flex-direction: column;
  width: 100%;
  margin-bottom: 0;
  & + & {
    margin-top: ${Spacing[4]};
  }
`;

export const InputsAccordion = styled(Accordion)`
  width: 100%;
  padding-top: ${Spacing[3]};
  padding-bottom: ${Spacing[3]};
  border-radius: ${BORDER_RADIUSES.rounded};
  background-color: ${({ theme }) => theme.color.background.primary};

  & > div:first-child {
    ${paragraphMixins.mdBold}
    padding-left: 0px;
    padding-right: 0px;
    padding-bottom: ${Spacing[3]};
  }
`;

const StyledRichTextEditor = styled(RichTextEditor)`
  width: 100%;
`;

export const TRANSLATIONS_DEBOUNCE_MILLISECONDS = 650;

export const isEmptyString = (value: string | string[] | undefined): boolean =>
  value == null || value === '';

export const handleTranslationUpdate = ({
  translations,
  value,
  locale,
}: {
  translations: Translation[];
  value: string;
  locale: InquiryIso6391LanguageCode;
}) => {
  return produce(translations, (draftTranslations) => {
    const index = draftTranslations.findIndex((t) => t.locale === locale);
    if (index === -1) {
      draftTranslations.push({
        locale: locale,
        value: value,
      });
    } else {
      draftTranslations[index] = {
        locale: locale,
        value: value,
      };
    }
  });
};

const getDefaultValue = ({
  stepLocalizedStrings,
  locale,
  sourceLocale,
  config,
}: {
  stepLocalizedStrings: StepLocalizedStrings;
  locale: InquiryIso6391LanguageCode;
  sourceLocale: InquiryIso6391LanguageCode;
  config: Omit<TranslationsConfig, 'path'>;
}): string => {
  const { stepName, name, clientKey, defaultValue } = config;
  /**
   * return client string defaultValue if source locale,
   * this value should always be present since the source locale's strings
   * have already been fetched
   */
  if (locale === sourceLocale) {
    return defaultValue;
  }

  // Not used for a combined step
  const localeStrings = stepLocalizedStrings?.[stepName.replace(/_/g, '-')]?.[locale] as {
    [componentName: string]: { [componentKey: string]: string };
  };

  // Check if locale hasn't been fetched
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  if (localeStrings == null) return null;

  // return cached client string for locale if it exists -
  // new components with text *do not* have server-provided localizations
  // because they haven't been persisted. otherwise return null
  return localeStrings[name.replace(/_/g, '-')]?.[clientKey] ?? null;
};

const getTranslation = (
  translations: Translation[] | null,
  locale: InquiryIso6391LanguageCode,
): Translation | undefined => translations?.find((t) => t.locale === locale);

interface TranslationInputsProps {
  label?: string;
  config: TranslationsConfig;
  currentLocale: InquiryIso6391LanguageCode;
  enabledLocales: InquiryIso6391LanguageCode[];
  editable: boolean;
  showCurrentLocale?: boolean;
  getStepLocalizedStrings: (locales?: Array<InquiryIso6391LanguageCode>) => Promise<void>;
  handleTranslationUpdates: (translationUpdates: Record<string, any>) => void;
}

export const TranslationInputs = ({
  label,
  config,
  currentLocale,
  enabledLocales,
  editable,
  showCurrentLocale = true,
  getStepLocalizedStrings,
  handleTranslationUpdates,
}: TranslationInputsProps) => {
  const translations = useSelector<RootState, Translation[]>(
    (state) => selector(state, `steps.${config.path}`) ?? [],
  );

  const stepLocalizedStrings = useSelector<RootState, StepLocalizedStrings>(
    (state) => state.inquiryTemplate.stepLocalizedStrings,
  );

  const prioritySortedLocales: InquiryIso6391LanguageCode[] = useMemo(() => {
    // prioritize current and most commonly spoken subset of enabled locales
    const primaryLocales = (showCurrentLocale ? [currentLocale] : []).concat(
      enabledLocales.filter(
        (locale) => FLE_COMMON_LOCALES.includes(locale) && locale !== currentLocale,
      ),
    );
    return primaryLocales.concat(
      xor(primaryLocales, enabledLocales).filter((locale) => locale !== currentLocale),
    );
  }, [currentLocale, enabledLocales, showCurrentLocale]);

  useEffect(() => {
    getStepLocalizedStrings(enabledLocales);
  }, []);

  const englishValue = getTranslation(translations, 'en-US')?.value;
  const translationSourceText = isEmptyString(englishValue)
    ? getDefaultValue({
        stepLocalizedStrings,
        locale: 'en-US',
        sourceLocale: currentLocale,
        config,
      })
    : englishValue;
  const isTranslatable =
    !isEmptyString(translationSourceText) && !Array.isArray(translationSourceText);

  const translationInputs = useMemo(() => {
    const inputs = prioritySortedLocales.map((locale, idx) => (
      <TranslationInput
        key={`${locale}-${idx}`}
        translationSourceText={translationSourceText}
        label={label ?? INQUIRY_LANGUAGES_ENGLISH[locale]}
        locale={locale}
        value={getTranslation(translations, locale)?.value}
        defaultValue={getDefaultValue({
          stepLocalizedStrings,
          locale,
          sourceLocale: currentLocale,
          config,
        })}
        // only use large display for source locale display within screen/component panel
        withLargeDisplay={enabledLocales.length === 1 && enabledLocales.includes(currentLocale)}
        withTranslateButton={
          isTranslatable && locale !== LOCALE_TO_EXCLUDE_TRANSLATIONS && locale !== currentLocale
        }
        editable={editable}
        onChange={(value, locale) => {
          handleTranslationUpdates({
            [config.translationsKey]: handleTranslationUpdate({
              translations: translations || [],
              value,
              locale,
            }),
          });
        }}
      />
    ));
    return (
      <>
        {inputs.length > 3
          ? inputs.slice(0, 3).concat([
              <InputsAccordion
                key='inputs-accordion'
                caretSize='md'
                title={`Show all (${inputs.length - 3})`}
                expandedTitle={`Hide additional (${inputs.length - 3})`}
                defaultExpanded={false}
                menuItemBorder={false}
                menuItemHover={false}
                menuItem
              >
                {inputs.slice(3)}
              </InputsAccordion>,
            ])
          : inputs}
      </>
    );
  }, [
    config,
    currentLocale,
    editable,
    enabledLocales,
    handleTranslationUpdates,
    isTranslatable,
    label,
    prioritySortedLocales,
    stepLocalizedStrings,
    translationSourceText,
    translations,
  ]);

  const { loading, requestTranslations } = useTranslations();
  const handleTranslateAll = useCallback(async () => {
    if (translationSourceText != null && !Array.isArray(translationSourceText)) {
      const newTranslations = await requestTranslations([
        [
          translationSourceText,
          enabledLocales.filter(
            (locale) => locale !== LOCALE_TO_EXCLUDE_TRANSLATIONS && locale !== currentLocale,
          ),
        ],
      ]);
      if (newTranslations[translationSourceText]) {
        let currentTranslations = translations;
        Object.entries(newTranslations[translationSourceText]).forEach(([locale, value]) => {
          currentTranslations = handleTranslationUpdate({
            translations: currentTranslations || [],
            value,
            locale: locale as InquiryIso6391LanguageCode,
          });
        });

        handleTranslationUpdates({
          [config.translationsKey]: currentTranslations,
        });
      }
    }
  }, [
    translationSourceText,
    requestTranslations,
    enabledLocales,
    translations,
    handleTranslationUpdates,
    config.translationsKey,
    currentLocale,
  ]);

  const hasMissingSourceText = isEmptyString(translationSourceText);
  const shouldShowUnsupportedLanguageText = enabledLocales.includes(LOCALE_TO_EXCLUDE_TRANSLATIONS);

  return (
    <>
      {editable && prioritySortedLocales.length > 1 && (
        <>
          {shouldShowUnsupportedLanguageText && (
            <SectionDescription>{`${UNSUPPORTED_LANGUAGE_TEXT}`}</SectionDescription>
          )}
          <TranslateAllButton
            id={translationSourceText as string}
            variant='basic'
            disabled={!editable}
            hasMissingSourceText={hasMissingSourceText}
            isPending={loading}
            handleTranslateAll={handleTranslateAll}
          />
        </>
      )}
      {translationInputs}
    </>
  );
};

interface TranslationInputProps {
  translationSourceText?: string | string[];
  label?: string;
  locale: InquiryIso6391LanguageCode;
  value: string | string[] | undefined;
  defaultValue: string | undefined;
  withLargeDisplay: boolean;
  withTranslateButton?: boolean;
  editable: boolean;
  onChange: (value: string, locale: InquiryIso6391LanguageCode) => void;
}

const TranslationInput = memo(
  ({
    translationSourceText,
    label,
    locale,
    value,
    defaultValue,
    withLargeDisplay = false,
    withTranslateButton = false,
    editable = true,
    onChange,
  }: TranslationInputProps) => {
    const isTyping = useRef<boolean>(false);

    // needed to set value on auto-translate
    useEffect(() => {
      // Mark the user as no longer typing once the value has been propagated
      debouncedStoppedTyping();

      // We don't want to update translationStateValue with the value/defaultValue from the props
      // while the user is typing in the input since the form update is debounced and the
      // value/defaultValue may be stale by the time it comes back
      if (isTyping.current) return;

      if (typeof value === 'string') {
        setTranslationStateValue(value);
        // instances where value/defaultValue are updated and value is undefined are during
        // 1) undo and 2) preview locale change. in these cases, we want to update translationStateValue
        // to defaultValue - either the prev (undo) value or the new preview locale value to trigger a re-render
      } else {
        setTranslationStateValue(defaultValue);
      }
    }, [defaultValue, value]);

    // if the value is undefined, the translation doesn't exist - in that case, we want to show the defaultValue.
    const coalescedValue = value ?? defaultValue;
    const [translationStateValue, setTranslationStateValue] = useState<string | undefined>(
      coalescedValue as string,
    );

    const debouncedUpdateTranslations = useCallback(
      debounce(TRANSLATIONS_DEBOUNCE_MILLISECONDS, onChange),
      [onChange],
    );
    const debouncedStoppedTyping = useCallback(
      debounce(TRANSLATIONS_DEBOUNCE_MILLISECONDS, () => (isTyping.current = false)),
      [],
    );

    return (
      <TranslationInputGroup
        label={label}
        locale={locale}
        translationSourceText={translationSourceText}
        inputProps={{
          size: 'md',
          value: translationStateValue,
          placeholder: defaultValue ?? placeholder,
          disabled: !editable,
          onBlur: () => {
            if (translationStateValue != null) {
              onChange(translationStateValue, locale);
            }
          },
          onChange: (e) => setTranslationStateValue(e.target.value),
        }}
        richTextEditorProps={{
          defaultValue: translationStateValue,
          menuBarSections: ['text-style', 'lists'],
          returnMarkdown: true,
          readOnly: !editable,
          onChange: (value) => {
            debouncedStoppedTyping.cancel();
            isTyping.current = true;

            // remove extra quotes returned when RichTextEditor interprets the current value
            // as markdown, e.g ###-###-#### placeholder for masked text input
            const strippedValue = value.replace(/\\/g, '');
            setTranslationStateValue(strippedValue);
            debouncedUpdateTranslations(strippedValue, locale);
          },
        }}
        onTranslationsRequest={(value: string) => onChange(value, locale)}
        withTranslateButton={withTranslateButton}
        withLargeDisplay={withLargeDisplay}
        withRichTextEditor
      />
    );
  },
);
TranslationInput.displayName = 'TranslationInput';

interface TranslationInputGroupProps {
  translationSourceText: string | string[] | undefined;
  locale: InquiryIso6391LanguageCode;
  label?: string;
  inputProps: ComponentProps<typeof TextArea> & ComponentProps<typeof Input>;
  richTextEditorProps?: ComponentProps<typeof RichTextEditor>;
  disabled?: boolean;
  withTranslateButton?: boolean;
  withLargeDisplay?: boolean;
  withRichTextEditor?: boolean;
  onTranslationsRequest?: (translation: string) => void;
}

const LargeDisplayContainer = styled.div`
  display: flex;
  position: relative;
`;

const LargeDisplayRightButton = styled.div`
  position: absolute;
  display: flex;
  right: ${Spacing[1]};
  top: 20px;
  transform: translateY(-50%);
  margin: 0;
  font-size: 0px;
`;

export const TranslationInputGroup = ({
  translationSourceText,
  locale,
  label,
  inputProps,
  richTextEditorProps,
  disabled = false,
  withTranslateButton = true,
  withLargeDisplay = false,
  withRichTextEditor = false,
  onTranslationsRequest,
}: TranslationInputGroupProps) => {
  const { loading, requestTranslations } = useTranslations();

  const button = withTranslateButton && onTranslationsRequest != null && (
    <Button
      id={`inquiry-template-editor-translation-input-translate-button-${locale}`}
      size='sm'
      type='button'
      variant='basic'
      onClick={async () => {
        if (translationSourceText != null && !Array.isArray(translationSourceText)) {
          const translations = await requestTranslations([[translationSourceText, [locale]]]);
          const translatedValue = translations[translationSourceText]?.[locale];
          if (translatedValue != null) {
            onTranslationsRequest(translatedValue);
          }
        }
      }}
      isPending={loading}
      disabled={disabled}
    >
      Translate
    </Button>
  );

  const displayTextEditor = () => {
    if (withLargeDisplay) {
      switch (true) {
        case withRichTextEditor:
          return <StyledRichTextEditor {...richTextEditorProps} />;
        case withTranslateButton:
          return (
            <LargeDisplayContainer>
              <TextArea {...inputProps} style={{ paddingRight: '72px' }} />
              <LargeDisplayRightButton>{button}</LargeDisplayRightButton>
            </LargeDisplayContainer>
          );
        default:
          return <TextArea {...inputProps} />;
      }
    } else {
      return (
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        <InputGroup size='md' rightButton={button}>
          <Input
            data-cy={`translation-panel__${locale}-input`}
            aria-labelledby={`translation-inputs__${locale}-input-label`}
            {...inputProps}
          />
        </InputGroup>
      );
    }
  };

  return (
    <StyledFormGroup key={`${locale}-${label}`}>
      {label && (
        <FormControlLabel id={`translation-inputs__${locale}-input-label`}>
          {label}
        </FormControlLabel>
      )}
      {displayTextEditor()}
    </StyledFormGroup>
  );
};

export default TranslationInputs;
